import { createContext, useContext, useState, FC, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import gameService from '../../services/game';
import questionService from '../../services/questions';
import { DEFAULT_CITY } from '../../routes';
import { useLanguage } from '../LanguageContext';
import { AvailableLanguages } from '../LanguageContext/types';
import usePlayerContext from '../PlayerContext';
import { ContextProps, ProviderProps } from './types';
import { GameSessionType, QuestionType } from '../../types/entities';

export const GameContext = createContext<ContextProps>({
  gameSession: undefined,
  currentQuestion: undefined,
  surveyQuestions: [],
  surveyQuestionsLoaded: false,
  currentSurveyQuestion: undefined,
  currentSurveyQuestionIndex: 0,
  surveyQuestionCount: 0,
  termsModalOpen: false,
  currentQuestionIndex: 0,
  isLoading: false,
  questionCount: 0,
  gameSessionRef: { current: undefined },
  createGameSession: async () => {},
  updateGameSessionAnswers: async () => undefined,
  updateSurveyAnswers: async () => undefined,
  setGameSession: () => {},
  setCurrentQuestionIndex: () => {},
  setCurrentSurveyQuestionIndex: () => {},
  setTermsModalOpen: () => {},
});

export const GameProvider: FC<ProviderProps> = ({ children }) => {
  const [gameSession, setGameSession] = useState<GameSessionType>();
  const gameSessionRef = useRef<GameSessionType>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [surveyQuestions, setSurveyQuestions] = useState<QuestionType[]>([]);
  const [surveyQuestionsLoaded, setSurveyQuestionsLoaded] = useState(false);
  const [currentSurveyQuestionIndex, setCurrentSurveyQuestionIndex] = useState(0);
  const surveyLocaleRef = useRef<AvailableLanguages | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const { player } = usePlayerContext();
  const { languageSelector } = useLanguage();
  const { city = DEFAULT_CITY } = useParams<{ city: string }>();

  useEffect(() => {
    if (surveyLocaleRef.current === languageSelector) {
      return;
    }

    let cancelled = false;
    setSurveyQuestionsLoaded(false);

    const fetchSurveyQuestions = async () => {
      try {
        const response = await questionService.getSurveyQuestions(languageSelector);
        if (cancelled) return;
        setSurveyQuestions(response.data);
        surveyLocaleRef.current = languageSelector;
        setCurrentSurveyQuestionIndex(0);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) {
          setSurveyQuestionsLoaded(true);
        }
      }
    };

    fetchSurveyQuestions();

    return () => {
      cancelled = true;
    };
  }, [languageSelector]);

  const createGameSession = async () => {
    setLoading(true);
    try {
      const randomQuestionsResponse = await questionService.getRandomQuestions(languageSelector);
      if (player) {
        const res = await gameService.createGameSession(
          player.documentId,
          randomQuestionsResponse.data.map((v) => v.documentId),
          city,
          languageSelector,
        );
        const newGameSession = {
          documentId: res.data.data.documentId,
          questions: randomQuestionsResponse.data,
          questionsAnswered: [],
          city,
        };
        setGameSession(newGameSession);
        gameSessionRef.current = newGameSession;
        setCurrentQuestionIndex(0);
        setCurrentSurveyQuestionIndex(0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const appendAnswer = async (questionDocumentId: string, answerId: number, time?: number) => {
    if (!gameSession || !questionDocumentId) return;

    setLoading(true);
    try {
      const updatedQuestionsAnswered = [
        ...(gameSession.questionsAnswered?.map((v) => ({
          question: v.question.documentId,
          answerId: v.answerId,
          time: v.time,
        })) || []),
        { question: questionDocumentId, answerId, time },
      ];

      const { data } = await gameService.updateGameSessionAnswers({
        gameSessionDocumentId: gameSession.documentId,
        questionsAnswered: updatedQuestionsAnswered,
      });

      const updatedGameSession = {
        ...gameSession,
        questionsAnswered: data.questionsAnswered,
        points: data.points,
      };

      gameSessionRef.current = updatedGameSession;
      setGameSession(updatedGameSession);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateGameSessionAnswers = (answerId: number, time: number) =>
    appendAnswer(gameSession?.questions[currentQuestionIndex]?.documentId || '', answerId, time);

  const updateSurveyAnswers = (answerId: number) =>
    appendAnswer(surveyQuestions[currentSurveyQuestionIndex]?.documentId || '', answerId);

  const currentQuestion = gameSession?.questions[currentQuestionIndex];
  const currentSurveyQuestion = surveyQuestions[currentSurveyQuestionIndex];

  return (
    <GameContext.Provider
      value={{
        gameSessionRef,
        questionCount: gameSession?.questions?.length ?? 0,
        surveyQuestionCount: surveyQuestions.length,
        gameSession,
        currentQuestion,
        surveyQuestions,
        surveyQuestionsLoaded,
        currentSurveyQuestion,
        currentSurveyQuestionIndex,
        termsModalOpen,
        currentQuestionIndex,
        isLoading,
        createGameSession,
        updateGameSessionAnswers,
        updateSurveyAnswers,
        setGameSession,
        setCurrentQuestionIndex,
        setCurrentSurveyQuestionIndex,
        setTermsModalOpen,
      }}>
      {children}
    </GameContext.Provider>
  );
};

export default function useGameContext(): ContextProps {
  return useContext(GameContext);
}
